import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import CountrySelector from '../components/CountrySelector';
import PriceTable from '../components/PriceTable';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { useGoldPrice } from '../hooks/useGoldPrice';
import { getCountryById } from '../utils/countries';
import { formatDateTime } from '../utils/formatters';
import { useSettings } from '../context/SettingsContext';
import GoldCalculator from '../components/GoldCalculator';

function Home() {
  const [activeCountry, setActiveCountry] = useState('egypt');
  const country = getCountryById(activeCountry);
  const { data, loading, error, refetch } = useGoldPrice(country?.currency);
  const { settings } = useSettings();

  return (
    <div className="home-page fade-in">
      <div className="container">
        <CountrySelector activeCountry={activeCountry} onSelect={setActiveCountry} />
        <div className="content-area">
          {loading && <LoadingSpinner />}
          {error && !loading && <ErrorMessage error={error} onRetry={refetch} />}
          {data && !loading && !error && (
            <div className="price-section">
              <PriceTable data={data} country={country} />
              <div className="footer-info" style={{ justifyContent: 'center' }}>
                <div className="last-updated" style={{ fontFamily: 'monospace', fontWeight: '600', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span>آخر تحديث:</span>
                  <span dir="ltr">
                    {(() => {
                      const d = new Date((data.timestamp < 10000000000 ? data.timestamp * 1000 : data.timestamp) || Date.now());
                      const day = d.getDate().toString().padStart(2, '0');
                      const month = (d.getMonth() + 1).toString().padStart(2, '0');
                      const year = d.getFullYear();
                      let hours = d.getHours();
                      const minutes = d.getMinutes().toString().padStart(2, '0');
                      const ampm = hours >= 12 ? 'مساءً' : 'صباحاً';
                      hours = hours % 12;
                      hours = hours ? hours : 12;
                      return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
                    })()}
                  </span>
                </div>
              </div>

              {/* Gold Calculator */}
              <GoldCalculator data={data} country={country} />

              {/* SEO FAQ Section */}
              <div className="seo-faq-section">
                <div className="faq-header">
                  <h2>الأسئلة الشائعة حول متابعة أسعار الذهب</h2>
                  <p>كل ما تحتاج معرفته عن تحديثات السوق وأعيرة الذهب</p>
                </div>
                
                <div className="faq-accordion">
                  <details className="faq-item" open>
                    <summary>
                      <span className="faq-question-text">كيف يمكنني متابعة <strong>اسعار الذهب لحظة بلحظة</strong>؟</span>
                      <span className="faq-icon"></span>
                    </summary>
                    <div className="faq-content">
                      <p>يوفر موقعنا تغطية حية وتحديثات مستمرة لجميع الأعيرة، لتتمكن من معرفة <strong>أسعار الذهب اليوم</strong> بدقة، سواء كنت تبحث عن <strong>سعر الذهب اليوم عيار 21 الآن</strong> أو غيره من الأعيرة المفضلة في السوق.</p>
                    </div>
                  </details>

                  <details className="faq-item">
                    <summary>
                      <span className="faq-question-text">ما هو <strong>سعر جرام الذهب عيار 21 اليوم</strong> وكيف يُحسب؟</span>
                      <span className="faq-icon"></span>
                    </summary>
                    <div className="faq-content">
                      <p>يعتبر عيار 21 الأكثر طلباً في الأسواق العربية. نحن نوفر لك <strong>أسعار الذهب اليوم عيار 21 الان</strong> بشكل دقيق بناءً على أسعار البورصة العالمية للسوق المحلي، لتتعرف على <strong>سعر جرام الذهب عيار 21 سعر الذهب اليوم</strong> بكل شفافية.</p>
                    </div>
                  </details>

                  <details className="faq-item">
                    <summary>
                      <span className="faq-question-text">هل يمكنني معرفة <strong>سعر الذهب اليوم عيار 18</strong> و<strong>سعر الجنيه الذهب</strong>؟</span>
                      <span className="faq-icon"></span>
                    </summary>
                    <div className="faq-content">
                      <p>بالتأكيد! جدول الأسعار بالأعلى يعرض لك <strong>اسعار الذهب الان</strong> بالتفصيل لجميع الأعيرة الرئيسية، بالإضافة إلى <strong>سعر الذهب اليوم عيار 18</strong> وقيمة <strong>سعر الجنيه الذهب</strong> لتتمكن من اتخاذ أفضل قرارات الشراء أو البيع.</p>
                    </div>
                  </details>

                  <details className="faq-item">
                    <summary>
                      <span className="faq-question-text">ماذا عن <strong>توقعات الذهب</strong> في الفترة القادمة؟</span>
                      <span className="faq-icon"></span>
                    </summary>
                    <div className="faq-content">
                      <p>يمكنك دائماً متابعة قسم المدونة الخاص بنا حيث نطرح تحليلات تساعدك في بناء <strong>توقعات الذهب</strong> المستقبلية بدقة ومعرفة <strong>جرام الذهب اليوم</strong> واتجاهات <strong>اسعار الذهب</strong> العالمية والمحلية.</p>
                    </div>
                  </details>
              </div>
            </div>

            {/* SEO Long Content Section */}
              <div className="seo-content-section" style={{ marginTop: '40px', padding: '30px', background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', border: 'var(--glass-border)', lineHeight: '1.8' }}>
                <h2 style={{ fontSize: '1.5rem', color: 'var(--color-gold-dark)', marginBottom: '20px', fontWeight: '800' }}>دليلك الشامل لمتابعة أسعار الذهب اليوم عيار 21 الآن</h2>
                
                <div className="quick-summary-box" style={{ background: 'var(--color-gold-light)', padding: '20px', borderRadius: 'var(--radius-md)', marginBottom: '25px', borderRight: '4px solid var(--color-gold)' }}>
                  <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: 'var(--color-gold-dark)' }}>ماذا يقدم لك موقع بكام الذهب؟</h3>
                  <ul style={{ paddingRight: '20px', listStyleType: 'square', color: 'var(--color-text-secondary)' }}>
                    <li>تحديث لحظي لـ <strong>سعر جرام الذهب عيار 21 اليوم</strong>.</li>
                    <li>تغطية شاملة لـ <strong>سعر الذهب اليوم عيار 18</strong> وجميع الأعيرة الأخرى.</li>
                    <li>متابعة دقيقة لقيمة <strong>سعر الجنيه الذهب</strong> وسبائك الذهب.</li>
                    <li>حاسبة ذهب متطورة تساعدك في معرفة القيمة المالية لمدخراتك.</li>
                  </ul>
                </div>

                <p style={{ marginBottom: '15px' }}>
                  تعتبر متابعة <strong>أسعار الذهب اليوم عيار 21 الان</strong> من أهم الخطوات التي يتخذها المستثمرون والمقبلون على الزواج في الوطن العربي، حيث يمثل عيار 21 العيار الأكثر تداولاً وطلباً في الأسواق المحلية. نحن نوفر لك في موقعنا تحديثاً دورياً ومباشراً لـ <strong>سعر جرام الذهب عيار 21 اليوم</strong>، مما يجعلك على اطلاع دائم بأي تغيرات تطرأ على السوق في لحظتها.
                </p>

                <p style={{ marginBottom: '15px' }}>
                  إذا كنت تبحث عن التنوع، فإننا نقدم لك أيضاً <strong>سعر الذهب اليوم عيار 18</strong> الذي يتميز بتصاميمه العصرية وسعره المناسب للكثيرين، بالإضافة إلى عرض مفصل لـ <strong>سعر الجنيه الذهب</strong> الذي يعد ملاذاً آمناً للادخار وحفظ قيمة الأموال من التضخم. إن معرفة <strong>اسعار الذهب الان</strong> تساعدك في اختيار التوقيت المثالي للشراء أو البيع بناءً على أرقام دقيقة وحقيقية.
                </p>

                <p style={{ marginBottom: '15px' }}>
                  لا تقتصر خدمتنا على عرض الأرقام فقط، بل نسعى لتقديم رؤية شاملة حول <strong>توقعات الذهب</strong> بناءً على حركة السوق العالمية والمحلية. سواء كنت تتابع <strong>اسعار الذهب</strong> بغرض الاستثمار طويل الأمد أو للاقتناء الشخصي، فإن وجود مصدر موثوق يعطيك <strong>جرام الذهب اليوم</strong> بكل شفافية هو أمر لا غنى عنه في ظل التقلبات الاقتصادية الحالية.
                </p>

                <h3 style={{ fontSize: '1.2rem', color: 'var(--color-text-primary)', marginBottom: '15px', fontWeight: '700' }}>لماذا يتغير سعر الذهب في الصاغة؟</h3>
                <p style={{ marginBottom: '15px' }}>
                  يتأثر <strong>سعر الذهب اليوم عيار 21 الآن</strong> بعدة عوامل أساسية، أولها سعر أوقية الذهب في البورصة العالمية، وثانيها سعر صرف العملة المحلية مقابل الدولار، بالإضافة إلى عامل العرض والطلب في السوق المحلي. نحن نقوم برصد هذه المتغيرات لحظة بلحظة لنقدم لك السعر الأدق الذي يتم تداوله في محلات الصاغة، شاملة أسعار البيع والشراء بدون المصنعية.
                </p>

                <h3 style={{ fontSize: '1.2rem', color: 'var(--color-text-primary)', marginBottom: '15px', fontWeight: '700' }}>نصائح عند شراء الذهب عيار 21 و18</h3>
                <p style={{ marginBottom: '15px' }}>
                  عند التفكير في الشراء، ننصحك دائماً بمتابعة <strong>تحديثات أسعار الذهب</strong> على مدار اليوم، حيث قد يشهد السعر تذبذباً ملحوظاً بين ساعات الصباح والمساء. تأكد دائماً من الحصول على فاتورة ضريبية مختومة تحتوي على تفاصيل الوزن والعيار والمصنعية، واحرص على متابعة موقعنا لمعرفة <strong>سعر الجنيه الذهب</strong> وسبائك الذهب إذا كان هدفك الأساسي هو الاستثمار والادخار.
                </p>

                <p>
                  نحن نلتزم بتقديم أدق البيانات حول <strong>أسعار الذهب اليوم</strong> لتكون شريكك الموثوق في عالم المعادن النفيسة. تذكر دائماً أن <strong>توقعات أسعار الذهب</strong> تتأثر بعدة عوامل عالمية مثل سعر الدولار وأسعار الفائدة، لذا ننصحك بمتابعة موقعنا بانتظام للحصول على آخر التحديثات وضمان حصولك على أفضل قيمة لاستثمارك. يمكنك أيضاً الإطلاع على أسعار الذهب العالمية عبر <a href="https://www.gold.org/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-gold-dark)', textDecoration: 'underline' }}>مجلس الذهب العالمي</a> لمزيد من المعلومات.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Home;
